import type { Breadcrumb, ErrorEvent, EventHint } from '@sentry/react'
import * as Sentry from '@sentry/react'
import { isInAppWebViewNoise, isOfflineError } from 'src/service/utils/sentry/filter'

export const SENTRY_SEVERITY = {
  Info: 'info',
  Warning: 'warning',
  Error: 'error',
} as const

// ─────────────────────────────────────────────
// 공통 유틸
// ─────────────────────────────────────────────

// ─────────────────────────────────────────────
// beforeSend
// ─────────────────────────────────────────────

export const beforeSend = (event: ErrorEvent, hint?: EventHint): ErrorEvent | null => {
  /**
   * Sentry로 이벤트가 전송되기 직전에 호출되는 필터/정제 함수
   * null을 반환하면 해당 이벤트는 Sentry로 전송되지 않음
   *
   * 문자열/regex 노이즈 필터는 SENTRY_IGNORE_ERRORS(ignoreErrors)에서 처리.
   * 여기서는 hint 기반 로직 필터만 담당.
   */

  // 무의미한 네트워크 에러 (사용자 인터넷 끊김)
  if (isOfflineError(hint)) return null

  // 인앱·광고 WebView 주입 스크립트 노이즈
  if (isInAppWebViewNoise(event)) return null

  const [, route, pCode] = window.location.pathname.split('/')
  event.tags = {
    ...event.tags,
    ...(route && { route }),
    ...(pCode && { pCode }),
  }

  return event
}

// ─────────────────────────────────────────────
// beforeBreadcrumb
// ─────────────────────────────────────────────

export const beforeBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb | null => {
  // console.log 제거
  if (breadcrumb.category === 'console') {
    return breadcrumb.level === SENTRY_SEVERITY.Error ? breadcrumb : null
  }
  // 성능 추적 breadcrumb 제거
  if (['sentry.transaction', 'sentry.performance'].includes(breadcrumb.category ?? '')) {
    return null
  }

  return breadcrumb
}

// ─────────────────────────────────────────────
// captureError
// ─────────────────────────────────────────────

/**
 * Sentry Scope에 공통 태그/extra를 적용
 */
const applyScopeMeta = (
  scope: Sentry.Scope,
  tags?: Record<string, string>,
  extra?: Record<string, unknown>,
) => {
  Object.entries(tags ?? {}).forEach(([k, v]) => scope.setTag(k, v))

  if (extra) {
    scope.setExtras(extra)
  }
}

interface CaptureErrorOptions {
  level?: Sentry.SeverityLevel
  tags?: Record<string, string>
  extra?: Record<string, unknown>
  fingerprint?: string[]
}

export const captureError = (error: unknown, options: CaptureErrorOptions = {}) => {
  const { level = SENTRY_SEVERITY.Error, tags, extra, fingerprint } = options

  Sentry.withScope((scope) => {
    scope.setLevel(level)
    applyScopeMeta(scope, tags, extra)
    if (fingerprint) {
      scope.setFingerprint(fingerprint)
    }
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)))
  })
}

// ─────────────────────────────────────────────
// captureMessage
// ─────────────────────────────────────────────

interface CaptureMessageOptions {
  level?: Sentry.SeverityLevel
  tags?: Record<string, string>
  extra?: Record<string, unknown>
}

export const captureMessage = (message: string, options: CaptureMessageOptions = {}) => {
  const { level = SENTRY_SEVERITY.Info, tags = {}, extra } = options

  Sentry.withScope((scope) => {
    scope.setLevel(level)
    applyScopeMeta(scope, tags, extra)

    Sentry.captureMessage(message, level)
  })
}

// ─────────────────────────────────────────────
// Error 타입 헬퍼
// ─────────────────────────────────────────────
export const toApiError = (error: Error): Error => {
  error.name = 'ApiError'
  return error
}

export const toServerError = (error: Error): Error => {
  error.name = 'ServerError'
  return error
}

export const toNavigationError = (error: Error): Error => {
  error.name = 'NavigationError'
  return error
}
